import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, MapPin, Calendar, Heart, GraduationCap } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { api, fileUrl } from "../lib/api";
import { SectionHeader, Chip } from "../components/ui-parts";

const HERO_IMG = "https://images.pexels.com/photos/6040175/pexels-photo-6040175.jpeg";
const MANUSCRIPT = "https://images.pexels.com/photos/37994572/pexels-photo-37994572.jpeg";
const STUDENTS = "https://images.pexels.com/photos/1720186/pexels-photo-1720186.jpeg";
const VOLUNTEERS_IMG = "https://images.pexels.com/photos/1720186/pexels-photo-1720186.jpeg";
const LIBRARY = "https://images.pexels.com/photos/13061431/pexels-photo-13061431.jpeg";

export default function Home() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [courses, setCourses] = useState([]);
  const [programmes, setProgrammes] = useState([]);

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
    api.get("/courses").then(r => setCourses(r.data.slice(0, 6))).catch(() => {});
    api.get("/programmes", { params: { status: "upcoming" } }).then(r => setProgrammes(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const logo = settings?.logo_url ? fileUrl(settings.logo_url) : null;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Odisha temple" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-narrow px-6 py-24 md:py-36 text-white">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                {logo ? <img src={logo} alt="logo" className="w-full h-full object-cover" /> : <span className="font-heading text-3xl">ॐ</span>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[var(--js-secondary)]">Odisha · India</div>
                <div className="font-devanagari text-lg opacity-90">जयतु संस्कृतम्</div>
              </div>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-tight" data-testid="hero-title">{t("org")}</h1>
            <p className="mt-5 text-lg md:text-xl text-white/90 max-w-2xl">This is the official website of Samskrita Bharati, Purva Utkal, dedicated to promoting spoken Sanskrit, Sanskrit courses, and cultural learning across Odisha.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/courses" className="js-btn-outline !text-white !border-white/60 hover:!bg-white hover:!text-[var(--js-primary)]" data-testid="cta-courses"><GraduationCap size={16} /> {t("cta.explore_courses")}</Link>
              <Link to="/volunteers" className="js-btn-outline !text-white !border-white/60 hover:!bg-white hover:!text-[var(--js-primary)]" data-testid="cta-volunteer"><Users size={16} /> {t("cta.join_volunteer")}</Link>
              <Link to="/programmes" className="js-btn-outline !text-white !border-white/60 hover:!bg-white hover:!text-[var(--js-primary)]" data-testid="cta-upcoming"><Calendar size={16} /> {t("cta.upcoming")}</Link>
              <Link to="/donate" className="js-btn-primary" style={{ background: "var(--js-accent)" }} data-testid="cta-donate"><Heart size={16} /> {t("cta.donate")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT / MISSION */}
      <section className="section" data-testid="about-brief">
        <div className="container-narrow grid gap-10 md:grid-cols-2 items-start">
          <div>
            <SectionHeader eyebrow="About" title={t("home.about_h")} description={settings?.about?.en} />
            <Link to="/about" className="js-btn-outline"><ArrowRight size={14} /> {t("common.read_more")}</Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="js-card p-6"><MapPin className="text-[var(--js-primary)] mb-3" /><div className="font-heading text-lg">30 Districts</div><p className="text-sm text-[var(--js-muted)] mt-1">Volunteers and activities being organised across Odisha.</p></div>
            <div className="js-card p-6"><BookOpen className="text-[var(--js-primary)] mb-3" /><div className="font-heading text-lg">Courses</div><p className="text-sm text-[var(--js-muted)] mt-1">Free & paid Sanskrit and spoken Sanskrit courses.</p></div>
            <div className="js-card p-6"><Users className="text-[var(--js-primary)] mb-3" /><div className="font-heading text-lg">Volunteers</div><p className="text-sm text-[var(--js-muted)] mt-1">Join a growing district-wise volunteer network.</p></div>
            <div className="js-card p-6"><Calendar className="text-[var(--js-primary)] mb-3" /><div className="font-heading text-lg">Programmes</div><p className="text-sm text-[var(--js-muted)] mt-1">Camps, workshops and cultural gatherings.</p></div>
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section className="section" style={{ background: "#F1EADD" }} data-testid="courses-section">
        <div className="container-narrow">
          <SectionHeader eyebrow="Learn" title="Free & Paid Courses" description="Enrol in structured Sanskrit courses designed for every learner — from complete beginners to advanced students." />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.length === 0 && <p className="text-[var(--js-muted)]">Courses will appear here soon.</p>}
            {courses.map(c => (
              <Link to={`/courses/${c.id}`} key={c.id} className="js-card p-6 block" data-testid={`course-card-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <Chip variant={c.is_free ? "free" : "paid"}>{c.is_free ? t("common.free") : t("common.paid")}</Chip>
                  <span className="text-xs text-[var(--js-muted)]">{c.mode}</span>
                </div>
                <div className="font-heading text-xl text-[var(--js-primary)]">{c.name}</div>
                <p className="text-sm text-[var(--js-muted)] mt-2 line-clamp-3">{c.description}</p>
                <div className="flex items-center gap-2 mt-4 text-xs text-[var(--js-muted)]"><MapPin size={12} />{c.district}</div>
              </Link>
            ))}
          </div>
          <div className="mt-8"><Link to="/courses" className="js-btn-primary" data-testid="view-all-courses">{t("common.view_all")} <ArrowRight size={14} /></Link></div>
        </div>
      </section>

      {/* PROGRAMMES */}
      <section className="section" data-testid="programmes-section">
        <div className="container-narrow">
          <SectionHeader eyebrow="Upcoming" title={t("home.upcoming_h")} description="Camps, training and cultural events happening across Odisha." />
          <div className="grid gap-6 md:grid-cols-3">
            {programmes.length === 0 && <p className="text-[var(--js-muted)]">No upcoming programmes yet.</p>}
            {programmes.map(p => (
              <div key={p.id} className="js-card p-6" data-testid={`programme-card-${p.id}`}>
                <div className="text-xs text-[var(--js-secondary)] font-semibold uppercase mb-2">{p.status}</div>
                <div className="font-heading text-xl text-[var(--js-primary)]">{p.name}</div>
                <div className="text-sm text-[var(--js-muted)] mt-2 flex items-center gap-2"><Calendar size={14} /> {p.date}</div>
                <div className="text-sm text-[var(--js-muted)] flex items-center gap-2"><MapPin size={14} /> {p.district}</div>
                <p className="text-sm mt-3 line-clamp-3">{p.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-8"><Link to="/programmes" className="js-btn-outline">{t("common.view_all")} <ArrowRight size={14} /></Link></div>
        </div>
      </section>

      {/* DISTRICT CTA */}
      <section className="section" style={{ background: "var(--js-accent)", color: "#fff" }} data-testid="district-cta">
        <div className="container-narrow grid gap-8 md:grid-cols-2 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--js-secondary)] font-semibold">Odisha Network</div>
            <h2 className="font-heading text-3xl md:text-4xl mt-2">Discover Sanskrit activities in your district</h2>
            <p className="mt-3 text-white/85 max-w-xl">Find volunteers, coordinators, courses and upcoming programmes across all 30 districts of Odisha.</p>
            <Link to="/districts" className="js-btn-primary mt-6" data-testid="district-explore-btn"><MapPin size={16} /> Explore Districts</Link>
          </div>
          <img src={VOLUNTEERS_IMG} alt="volunteers" className="rounded-lg object-cover w-full h-64 md:h-80" />
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="section" style={{ background: "#F1EADD" }} data-testid="donate-cta">
        <div className="container-narrow text-center">
          <div className="text-xs uppercase tracking-widest text-[var(--js-secondary)] font-semibold">Support</div>
          <h2 className="font-heading text-3xl md:text-4xl mt-2 text-[var(--js-primary)]">{t("home.donate_cta")}</h2>
          <p className="mt-3 text-[var(--js-muted)] max-w-2xl mx-auto">Your donation helps us organise free Sanskrit camps, print teaching materials, and expand our volunteer network across Odisha.</p>
          <div className="mt-6"><Link to="/donate" className="js-btn-primary" data-testid="donate-cta-btn"><Heart size={16} /> {t("cta.donate")}</Link></div>
        </div>
      </section>
    </div>
  );
}
